import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { createStore, useStore, type StoreApi } from 'zustand';
import { BulkOperationStatus } from 'api/generated/types';

import { BulkOperationToast } from '../components/BulkOperations/BulkOperationToast';
import { bulkOperationErrorText } from '../components/BulkOperations/bulkOperationErrorText';
import { bulkOperationService } from '../api/services/bulkOperationService';
import { type StreamController } from '../api/services/streamController';
import type { OperationProgress } from './bulkOperationTypes';

export type { OperationProgress } from './bulkOperationTypes';

interface BulkOperationContextValue {
  operations: Map<string, OperationProgress>;
  trackOperation: (operationId: string, title?: string) => void;
  removeOperation: (operationId: string) => void;
}

const BulkOperationContext = createContext<
  StoreApi<BulkOperationContextValue> | undefined
>(undefined);

function createBulkOperationStore() {
  const controllers = new Map<string, StreamController>();
  return createStore<BulkOperationContextValue>()((set) => {
    const patchOperation = (
      operationId: string,
      patch: Partial<OperationProgress>
    ) => {
      set((state) => {
        const current = state.operations.get(operationId);
        // A late event from a dismissed operation must not recreate it.
        if (!current) return state;
        const operations = new Map(state.operations);
        operations.set(operationId, { ...current, ...patch });
        return { operations };
      });
    };

    return {
      operations: new Map(),
      trackOperation: (operationId, title) => {
        if (controllers.has(operationId)) return;

        set((state) => ({
          operations: new Map(state.operations).set(operationId, {
            operationId,
            title,
            status: BulkOperationStatus.PENDING,
            totalCount: 0,
            processedCount: 0,
            successCount: 0,
            failedCount: 0,
            error: null,
          }),
        }));

        const controller = bulkOperationService.streamProgress(operationId, {
          onOperationStarted: (data) => {
            patchOperation(operationId, {
              status: data.status,
              totalCount: data.total_count,
              processedCount: data.processed_count,
              successCount: data.success_count,
              failedCount: data.failed_count,
            });
          },
          onItemProcessed: (data) => {
            patchOperation(operationId, {
              processedCount: data.processed_count,
              successCount: data.success_count,
            });
          },
          onItemFailed: (data) => {
            patchOperation(operationId, {
              processedCount: data.processed_count,
              failedCount: data.failed_count,
            });
          },
          onOperationCompleted: (data) => {
            patchOperation(operationId, {
              status: data.status,
              totalCount: data.total_count,
              processedCount: data.processed_count,
              successCount: data.success_count,
              failedCount: data.failed_count,
            });
            controllers.delete(operationId);
          },
          onError: (streamError) => {
            console.error('[bulk-operations] stream error', streamError);
            patchOperation(operationId, {
              status: BulkOperationStatus.FAILED,
              error: bulkOperationErrorText(streamError),
            });
            controllers.delete(operationId);
          },
        });
        controllers.set(operationId, controller);
      },
      removeOperation: (operationId) => {
        controllers.get(operationId)?.cancel();
        controllers.delete(operationId);
        set((state) => {
          if (!state.operations.has(operationId)) return state;
          const operations = new Map(state.operations);
          operations.delete(operationId);
          return { operations };
        });
      },
    };
  });
}

/** Each provider owns its operations and SSE connections. */
export const BulkOperationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [store] = useState(createBulkOperationStore);

  return (
    <BulkOperationContext.Provider value={store}>
      {children}
      <BulkOperationToasts />
    </BulkOperationContext.Provider>
  );
};

function BulkOperationToasts() {
  const operations = useBulkOperation((state) => state.operations);
  const removeOperation = useBulkOperation((state) => state.removeOperation);
  return (
    <>
      {Array.from(operations.values()).map((operation) => (
        <BulkOperationToast
          key={operation.operationId}
          operation={operation}
          onClose={() => removeOperation(operation.operationId)}
        />
      ))}
    </>
  );
}

/** Subscribe only to the actions or operation state the caller uses. */
export const useBulkOperation = <T,>(
  selector: (state: BulkOperationContextValue) => T
): T => {
  const store = useContext(BulkOperationContext);
  if (!store) {
    throw new Error(
      'useBulkOperation must be used within a BulkOperationProvider'
    );
  }
  return useStore(store, selector);
};
